import PropTypes from 'prop-types';
import { Icon, Button } from '@evertrue/et-components';

/**
 * Condition step card for the cadence flow editor.
 * Redesigned with purple badge, CONDITION label, condition name display,
 * and CONDITIONAL badge to match branching flow design.
 */
const ConditionStepCard = ({ condition, onEdit, onDelete }) => {
  const getConditionName = () => {
    if (!condition?.type) return 'Set condition...';
    switch (condition.type) {
      case 'email_opened':
        return 'Email Opened?';
      case 'email_replied':
        return 'Responded to Email?';
      case 'email_clicked':
        return 'Email Link Clicked?';
      case 'interaction_created':
        return 'Had Interaction';
      case 'gift_given':
        return 'Made a Gift';
      case 'portfolio_stage_reached':
        return 'Moved into Portfolio Stage';
      case 'saved_search':
        return 'Qualifies for Saved Search';
      case 'list_membership':
        return 'Is in List';
      default:
        return condition.label || condition.type;
    }
  };

  const getConditionDetails = () => {
    if (!condition?.type) return [];
    switch (condition.type) {
      case 'interaction_created':
        return condition.interactionType ? [{ label: 'Type', value: condition.interactionType }] : [];
      case 'gift_given': {
        const details = [];
        if (condition.minAmount) details.push({ label: 'Amount', value: `≥ $${condition.minAmount}` });
        if (condition.designation) details.push({ label: 'Designation', value: condition.designation });
        return details;
      }
      case 'portfolio_stage_reached': {
        const details = [];
        if (condition.portfolioTeamName) details.push({ label: 'Team', value: condition.portfolioTeamName });
        if (condition.portfolioStage) details.push({ label: 'Stage', value: condition.portfolioStage });
        return details;
      }
      case 'saved_search':
        return condition.savedSearchName ? [{ label: 'Search', value: condition.savedSearchName }] : [];
      case 'list_membership':
        return condition.listName ? [{ label: 'List', value: condition.listName }] : [];
      default:
        return [];
    }
  };

  const getConditionDetail = () => {
    if (!condition?.notMetAction) {
      return 'Check if condition is met to branch flow';
    }

    switch (condition.notMetAction) {
      case 'wait':
        return `Wait ${condition.waitDays || 3} days, then check again`;
      case 'end':
        return 'End cadence if not met';
      case 'continue':
        return 'Continue to next step';
      default:
        return 'Check if condition is met to branch flow';
    }
  };

  const details = getConditionDetails();

  return (
    <div className="condition-step-card">
      <div className="condition-step-card--header">
        <div className="condition-step-card--header-left">
          <span className="condition-step-card--badge">
            <Icon icon="filter-list" />
            CONDITION
          </span>
        </div>
        <div className="condition-step-card--actions" onClick={(e) => e.stopPropagation()}>
          <Button type="simple" onClick={onEdit} title="Edit condition">
            <Icon icon="edit" />
          </Button>
          <Button type="simple" onClick={onDelete} title="Remove condition">
            <Icon icon="delete" />
          </Button>
        </div>
      </div>

      <div className="condition-step-card--body">
        <button type="button" className="condition-step-card--condition-name" onClick={onEdit}>
          <span className="condition-step-card--condition-text">{getConditionName()}</span>
          <Icon icon="chevron-right" />
        </button>
        {details.length > 0 && (
          <div className="condition-step-card--details">
            {details.map(({ label, value }) => (
              <div key={label} className="condition-step-card--detail-row">
                <span className="condition-step-card--detail-label">{label}:</span>
                <span className="condition-step-card--detail-value">{value}</span>
              </div>
            ))}
          </div>
        )}
        <span className="condition-step-card--conditional-badge">CONDITIONAL</span>
        <p className="condition-step-card--detail">{getConditionDetail()}</p>
      </div>
    </div>
  );
};

ConditionStepCard.propTypes = {
  condition: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    label: PropTypes.string,
    interactionType: PropTypes.string,
    minAmount: PropTypes.string,
    designation: PropTypes.string,
    portfolioTeamId: PropTypes.string,
    portfolioTeamName: PropTypes.string,
    portfolioStage: PropTypes.string,
    savedSearch: PropTypes.string,
    savedSearchName: PropTypes.string,
    list: PropTypes.string,
    listName: PropTypes.string,
    waitDays: PropTypes.number,
    notMetAction: PropTypes.oneOf(['wait', 'end', 'continue']),
  }),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ConditionStepCard;
